import { useEffect, useState, useCallback } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Undo2, Trash2 } from "lucide-react";

export default function AdminTrash() {
  const [trash, setTrash] = useState({ users: [], invoices: [], orders: [] });

  const load = useCallback(() => api.get("/admin/trash").then((r) => setTrash(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const restore = async (kind, id) => {
    try { await api.post(`/admin/${kind}/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const purge = async (kind, id) => {
    if (!window.confirm("Permanently delete this record? This cannot be undone.")) return;
    try { await api.delete(`/admin/${kind}/${id}/permanent`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const sections = [
    { key: "users", label: "Customers", cols: ["Name", "Email", "Deleted"], render: (u) => [u.name || "—", u.email, new Date(u.deleted_at).toLocaleString()] },
    { key: "invoices", label: "Invoices", cols: ["Email", "Total", "Status", "Deleted"], render: (i) => [i.user_email, inr(i.total), i.status, new Date(i.deleted_at).toLocaleString()] },
    { key: "orders", label: "Orders", cols: ["Email", "Items", "Deleted"], render: (o) => [o.user_email, o.items, new Date(o.deleted_at).toLocaleString()] },
  ];

  const totalCount = trash.users.length + trash.invoices.length + trash.orders.length;

  return (
    <div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-2" data-testid="admin-trash-title">Trash</h1>
      <p className="text-sm text-gray-500 mb-6">{totalCount} soft-deleted record{totalCount === 1 ? "" : "s"}. Restore brings them back exactly as they were.</p>

      {sections.map((s) => (
        <div key={s.key} className="mb-8">
          <h2 className="font-heading font-medium mb-2 text-sm uppercase tracking-wider text-gray-600">{s.label} ({trash[s.key].length})</h2>
          <div className="border border-gray-200">
            <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600 font-medium">
              {s.cols.map((c, i) => (<div key={c} className={i === s.cols.length - 1 ? "col-span-3" : "col-span-3"}>{c}</div>))}
              <div className="col-span-3 text-right">Action</div>
            </div>
            {trash[s.key].length === 0 && <div className="p-4 text-sm text-gray-400">Empty</div>}
            {trash[s.key].map((row) => (
              <div key={row.id} className="grid grid-cols-12 px-4 py-3 border-b border-gray-100 items-center text-sm" data-testid={`trash-${s.key}-${row.id}`}>
                {s.render(row).map((cell, i) => (<div key={`${row.id}-${i}`} className="col-span-3 truncate text-gray-700">{cell}</div>))}
                <div className="col-span-3 text-right flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => restore(s.key, row.id)} data-testid={`trash-restore-${row.id}`} className="rounded-sm">
                    <Undo2 className="w-3 h-3 mr-1" strokeWidth={1.5}/> Restore
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => purge(s.key, row.id)} data-testid={`trash-delete-${row.id}`} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="w-3 h-3 mr-1" strokeWidth={1.5}/> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
